import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

export interface ConsumerListItem {
  id: string; name: string; phone: string; city: string; tokens: number; lastVisit: string; status: string;
}
export interface TokenListItem {
  id: string; number: string; consumer: string; queue: string; business: string;
  status: string; time: string; amount: number; date: string;
}

interface ConsumersState {
  items: ConsumerListItem[];
  filters: { cities: string[]; statuses: string[] };
  loading: boolean;
  error: string | null;
}

export function useConsumers() {
  const [state, setState] = useState<ConsumersState>({
    items: [], filters: { cities: [], statuses: [] }, loading: true, error: null,
  });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.CONSUMERS, { params: { page: 1, page_size: 100 } });
      const data = res.data?.data ?? res.data;
      setState({ items: data.items, filters: data.filters, loading: false, error: null });
    } catch (err: any) {
      setState((s) => ({ ...s, loading: false, error: err?.response?.data?.message || 'Failed to load consumers.' }));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

interface TokensState {
  items: TokenListItem[];
  filters: { statuses: string[] };
  loading: boolean;
  error: string | null;
}

export function useTokens() {
  const [state, setState] = useState<TokensState>({ items: [], filters: { statuses: [] }, loading: true, error: null });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.TOKENS, { params: { page: 1, page_size: 100 } });
      const data = res.data?.data ?? res.data;
      setState({ items: data.items, filters: data.filters, loading: false, error: null });
    } catch (err: any) {
      setState((s) => ({ ...s, loading: false, error: err?.response?.data?.message || 'Failed to load tokens.' }));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

export interface ConsumerDetail {
  consumer: Record<string, any>;
  stats: { total: number; completed: number; cancelled: number; no_show: number; businesses_visited: number };
  tokens: TokenListItem[];
  consultations: Array<Record<string, any>>;
}

export function useConsumerDetail(id: string | undefined) {
  const [data, setData] = useState<ConsumerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`${API_ENDPOINTS.CONSUMERS}/${id}`);
      setData((res.data?.data ?? res.data) as ConsumerDetail);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load consumer.');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  return { data, loading, error, reload: load };
}
